import { api } from 'api';
import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import { DESKTOP_MIN_WIDTH, EDIT_RECEIVER_STATE_NAME } from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';

const SUBURBS_SEARCH_NAME = 'suburbs-search-field';

const CustomSuburbsDropdown = ({
  ref,
  field,
  isConfigRequired,
  information,
  setInformation,
  validation,
  setValidation,
}) => {
  const { t } = useLang();

  const [searchValue, setSearchValue] = useState('');
  const [suburbsSearch, setSuburbsSearch] = useState(null);
  const [currentSuburbSelected, setCurrentSuburbSelected] = useState(null);
  const [suburbs, setSuburbs] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isSearchInvalid = !searchTextValid(searchValue);

  const fetchSuburbs = async (stateId) => {
    try {
      const { data } = await api.getSuburbs(stateId);

      if (data) {
        setCurrentSuburbSelected(null);

        setSuburbs(data);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };

  useEffect(() => {
    if (information[EDIT_RECEIVER_STATE_NAME]?.id) {
      fetchSuburbs(information[EDIT_RECEIVER_STATE_NAME].id);
    }
  }, [information[EDIT_RECEIVER_STATE_NAME]?.id]);

  useEffect(() => {
    if (isEmpty(suburbs)) return;
    if (suburbs) {
      const suburb = suburbs?.find(
        (c) =>
          c?.name &&
          information[field.name] &&
          c?.name?.trim() === information[field.name]?.name?.trim()
      );

      if (suburb) {
        setCurrentSuburbSelected(suburb);

        const newInformation = {
          ...information,
          [field.name]: suburb,
        };
        setInformation(newInformation);
      }
    }
  }, [suburbs]);

  useInputFocus(SUBURBS_SEARCH_NAME);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setSuburbsSearch(null);

      return;
    }

    const newSuburbSearch = suburbs?.filter(
      (suburb) =>
        suburb?.name?.toLowerCase()?.includes(value?.toLowerCase()) && suburb
    );
    setSuburbsSearch(newSuburbSearch);
  };

  const handleSuburbSelected = (suburb) => {
    setCurrentSuburbSelected(suburb);

    setSearchValue('');

    setSuburbsSearch(null);

    setShowDropdown(false);

    const newInformation = {
      ...information,
      [field.name]: suburb,
    };
    setInformation(newInformation);

    const newValidation = {
      ...validation,
      [field.name]: '',
    };
    setValidation(newValidation);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');
    setSuburbsSearch(null);
    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        ref={ref}
        name={SUBURBS_SEARCH_NAME}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{ borderRadius: '0 0 8px 8px', paddingBottom: 0 }}
      >
        {renderDropdownItems(isSearchInvalid ? [] : suburbsSearch || suburbs)}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
        >
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{t('form_text_no_result')}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.id}
            eventKey={index}
            onClick={() => handleSuburbSelected(item)}
          >
            {item.name}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  return (
    <SuburbDropdownStyled>
      <Label>
        {t(field.label)}
        {(isConfigRequired ?? field.isRequired) && <span>*</span>}
      </Label>
      <DropdownCommon
        value={currentSuburbSelected?.name}
        placeholder={field.placeholder}
        keyError={validation[field.name]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
    </SuburbDropdownStyled>
  );
};

const SuburbDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  margin-bottom: 8px;

  & span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

export default CustomSuburbsDropdown;
